import type { Session, SessionEvent, SessionEventKind } from '@orgus/protocol';
import { SESSION_EVENT_KIND_LABEL } from '@orgus/protocol';
import { Film, List, ListFilter, Search, X } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  DiffViewer,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  GROUP_INDENT,
  GroupChevron,
  IconButton,
  Input,
  LiveBadge,
  Pressable,
  Segmented,
  SkeletonRows,
  cn,
  formatClock,
  formatDuration,
  formatInt,
  plural,
} from '@orgus/ui';
import { useExt } from '../../lib/store.ts';
import { useSessionEvents } from './api.ts';
import { EventStatus, KindIcon, KIND_SINGULAR } from './EventLook.tsx';
import { approvalEvents, combineEvents, editDiffFile, eventCounts, filterEvents } from './model.ts';
import { Replay } from './Replay.tsx';

/** Kinds offered as filter chips, in this order (system lines stay in the list but get no chip). */
const CHIPS: SessionEventKind[] = ['prompt', 'tool', 'subagent', 'approval', 'compaction', 'error', 'reply'];

function EventRow({ e }: { e: SessionEvent }) {
  const [open, setOpen] = useState(false);
  const more = !!(e.detail || e.edit || e.meta);
  const line = (
    <>
      <GroupChevron open={open} className={cn(!more && 'invisible')} />
      <KindIcon kind={e.kind} />
      <span className="sr-only">{KIND_SINGULAR[e.kind]}: </span>
      <span className={cn('min-w-0 flex-1 truncate', e.kind === 'reply' ? 'text-fg-muted' : 'text-fg')}>{e.title}</span>
      {e.meta && e.kind === 'subagent' && <Badge className="shrink-0">{e.meta}</Badge>}
      {/* On a phone the title keeps the room: a done step needs no status, and its time says when. */}
      <EventStatus e={e} className={e.status === 'ok' ? 'max-md:hidden' : undefined} />
      {e.durationMs != null && e.durationMs >= 1000 && <span className="w-16 shrink-0 text-right text-caption text-fg-subtle num max-md:hidden">{formatDuration(e.durationMs)}</span>}
      <span className="w-16 shrink-0 text-right text-caption text-fg-subtle num">{e.at != null ? formatClock(e.at) : ''}</span>
    </>
  );
  const row = 'flex h-row w-full min-w-0 items-center gap-2 px-3 text-left text-ui';
  return (
    <li data-row="">
      {more ? (
        <Pressable onClick={() => setOpen((o) => !o)} aria-expanded={open} className={cn(row, 'hover:bg-hover')}>
          {line}
        </Pressable>
      ) : (
        <div className={row}>{line}</div>
      )}
      {open && (
        // What opens sits one group step in, under the event's icon (GROUP_INDENT).
        <div className="px-3 pb-3">
          <div className={cn('flex flex-col gap-2', GROUP_INDENT)}>
            {e.meta && e.kind !== 'subagent' && <p className="text-caption text-fg-subtle">{e.meta}</p>}
            {e.detail && <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-control bg-surface px-3 py-2 font-mono text-caption text-fg-muted">{e.detail}</pre>}
            {e.edit && <DiffViewer files={[editDiffFile(e.edit)]} bare />}
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * The events filter as one quiet menu beside the search: each kind with its count, and failed or
 * refused steps. The button says how many filters narrow the list.
 */
function EventFilter({
  counts,
  kinds,
  onToggle,
  problems,
  onProblems,
  onClear,
}: {
  counts: Record<SessionEventKind, number>;
  kinds: SessionEventKind[];
  onToggle: (k: SessionEventKind) => void;
  problems: boolean;
  onProblems: () => void;
  onClear: () => void;
}) {
  const active = kinds.length + (problems ? 1 : 0);
  const keep = (e: Event) => e.preventDefault();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant={active ? 'secondary' : 'ghost'} icon={<ListFilter />}>
          Filter
          {active > 0 && <span className="text-fg-subtle num">{formatInt(active)}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {CHIPS.filter((k) => counts[k] > 0).map((k) => (
          <DropdownMenuCheckboxItem
            key={k}
            checked={kinds.includes(k)}
            onSelect={(e) => {
              keep(e);
              onToggle(k);
            }}
          >
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <KindIcon kind={k} />
              <span className="min-w-0 flex-1 truncate">{SESSION_EVENT_KIND_LABEL[k]}</span>
              <span className="text-caption text-fg-subtle num">{formatInt(counts[k])}</span>
            </span>
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={problems}
          onSelect={(e) => {
            keep(e);
            onProblems();
          }}
        >
          Failed or refused
        </DropdownMenuCheckboxItem>
        {active > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem icon={<X />} onSelect={onClear}>
              Clear filters
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The session's steps as a filterable list, or replayed step by step with the scrubber. */
export function EventsTab({ session, live }: { session: Session; live: boolean }) {
  const src = useSessionEvents(session, live);
  const approvals = useExt<unknown>('approvals');
  const [view, setView] = useState<'list' | 'replay'>('list');
  const [kinds, setKinds] = useState<SessionEventKind[]>([]);
  const [query, setQuery] = useState('');
  const [problems, setProblems] = useState(false);

  const all = useMemo(() => combineEvents(src.data?.events ?? [], approvalEvents(approvals, session.id)), [src.data?.events, approvals, session.id]);
  const counts = useMemo(() => eventCounts(all), [all]);
  const shown = useMemo(() => filterEvents(all, { kinds, query, problems }), [all, kinds, query, problems]);
  const filtered = kinds.length > 0 || !!query.trim() || problems;
  const toggle = (k: SessionEventKind) => setKinds((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]));

  if (src.loading) {
    return (
      <div className="flex flex-col gap-3 py-2">
        <SkeletonRows rows={8} />
      </div>
    );
  }
  if (src.error && !src.data) return <ErrorState error={src.error} onRetry={src.reload} />;
  if (!all.length) {
    return (
      <EmptyState
        icon={<List />}
        title="No events yet"
        description="Prompts, tool calls and subagents show here as the session works"
        action={
          <Button size="sm" onClick={src.reload}>
            Check again
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented<'list' | 'replay'>
          label="Events view"
          value={view}
          onChange={setView}
          options={[
            { value: 'list', label: 'List', icon: <List /> },
            { value: 'replay', label: 'Replay', icon: <Film /> },
          ]}
        />
        <span className="text-caption text-fg-subtle num">{plural(all.length, 'event')}</span>
        {live && <LiveBadge mode="live" />}
        {src.refreshing && <span className="sr-only">Updating</span>}
      </div>
      {src.error && <ErrorState variant="inline" error={src.error} onRetry={src.reload} />}
      {(src.data?.partial || (src.data?.omitted ?? 0) > 0) && (
        <p role="note" className="rounded-card border border-border-subtle bg-surface px-3 py-2 text-ui text-fg-muted">
          {src.data?.partial
            ? 'This transcript is long, so only its most recent part was read. Earlier events are not listed.'
            : `${formatInt(src.data?.omitted ?? 0)} earlier events are not listed (the list keeps the latest 2,000).`}
        </p>
      )}

      {view === 'replay' ? (
        <Replay events={all} />
      ) : (
        <>
          <div className="flex min-w-0 items-center gap-2">
            <Input
              size="sm"
              icon={<Search />}
              placeholder="Search events"
              aria-label="Search events"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              wrapperClassName="min-w-0 flex-1"
              trailing={
                query ? (
                  <IconButton size="xs" variant="ghost" label="Clear the search" icon={<X />} onClick={() => setQuery('')} />
                ) : undefined
              }
            />
            <EventFilter
              counts={counts}
              kinds={kinds}
              onToggle={toggle}
              problems={problems}
              onProblems={() => setProblems((p) => !p)}
              onClear={() => {
                setKinds([]);
                setProblems(false);
              }}
            />
          </div>
          {shown.length ? (
            <ol data-list="" className="overflow-hidden rounded-card border border-border-subtle bg-surface py-1" aria-label="Session events">
              {shown.map((e) => (
                <EventRow key={`${e.seq}`} e={e} />
              ))}
            </ol>
          ) : (
            <EmptyState
              icon={<ListFilter />}
              title="No events match"
              description={filtered ? 'Nothing in this session matches these filters.' : 'There is nothing to show.'}
              action={
                <Button
                  size="sm"
                  onClick={() => {
                    setKinds([]);
                    setQuery('');
                    setProblems(false);
                  }}
                >
                  Clear filters
                </Button>
              }
            />
          )}
        </>
      )}
    </div>
  );
}
